import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { paywall } from "scmp-app/data";
import { page as PlusSearchPage } from "scmp-app/pages/plus/search";
import {
  page as SearchPage,
  type RouteFunction as SearchRouteFunction,
} from "scmp-app/pages/search";
import type { ArticlesSearchOrderByInput } from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";

export type ArticlesSearchFilterByDateRange = {
  endDate?: number;
  startDate?: number;
};

type SearchPageType = "default" | "plus";

type ContextValue = {
  currentValue: string;
  dateRange?: ArticlesSearchFilterByDateRange;
  excludePaywallTypeIds?: string[];
  orderBy?: ArticlesSearchOrderByInput;
  paywallTypeIds?: string[];
  searchPageType: SearchPageType;
  searchRouteFunction?: SearchRouteFunction;
  sectionIds?: string[];
  setDateRange?: Dispatch<SetStateAction<ArticlesSearchFilterByDateRange | undefined>>;
  setExcludePaywallTypeIds?: Dispatch<SetStateAction<string[]>>;
  setOrderBy?: Dispatch<SetStateAction<ArticlesSearchOrderByInput | undefined>>;
  setPaywallTypeIds?: Dispatch<SetStateAction<string[]>>;
  setSectionIds?: Dispatch<SetStateAction<string[]>>;
};

export const SearchContext = createContext<ContextValue>({
  currentValue: "",
  searchPageType: "default",
});

type Props = {
  children: ReactNode;
  currentValue: string;
  searchPageType: SearchPageType;
};

export const SearchContextProvider: React.FunctionComponent<Props> = ({
  children,
  currentValue,
  searchPageType,
}) => {
  const [orderBy, setOrderBy] = useState<ArticlesSearchOrderByInput>();
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const [paywallTypeIds, setPaywallTypeIds] = useState<string[]>([]);
  const [excludePaywallTypeIds, setExcludePaywallTypeIds] = useState<string[]>(() =>
    searchPageType === "default" ? [paywall.scmpPlus.entityUuid] : [],
  );
  const [dateRange, setDateRange] = useState<ArticlesSearchFilterByDateRange | undefined>();

  const searchRouteFunction = useMemo(
    () => (searchPageType === "default" ? SearchPage.route : PlusSearchPage.route),
    [searchPageType],
  );

  useEffect(() => {
    if (currentValue) return;
    // Clear effect when the search input has been cleared
    setOrderBy(undefined);
    setSectionIds([]);
    setPaywallTypeIds([]);
    setExcludePaywallTypeIds(searchPageType === "default" ? [paywall.scmpPlus.entityUuid] : []);
    setDateRange(undefined);
  }, [currentValue, searchPageType]);

  return (
    <SearchContext.Provider
      value={{
        currentValue,
        dateRange,
        excludePaywallTypeIds,
        orderBy,
        paywallTypeIds,
        searchPageType,
        searchRouteFunction,
        sectionIds,
        setDateRange,
        setExcludePaywallTypeIds,
        setOrderBy,
        setPaywallTypeIds,
        setSectionIds,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);
